import React, { Fragment, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import IncomeItem from '@components/ssap/pages/pageIncludes/summary/IncomeItem';
import SubHeader from '@components/ssap/headers/SubHeader';

// Context

import { Context } from '@contexts/ssapSummaryContext';

// Hooks

import { useSummaryItemDiffRegister } from '@hooks/useSummaryItemDiffRegister';

// Utils

import { currencyTools } from '@utils/tools/currencyTools';
import { filterTools } from '@utils/tools/filterTools';
import { isEqual, pick } from '@utils/miscellaneous/objectHelpers';

const TribalIncomeSummarySection = props => {
    const {
        applicantGuid,
        incomes,
        isFinalSummaryPage,
        isSsapInReviewMode,
        lists: {
            FREQUENCIES_LIST,
            INCOME_SOURCES_LIST
        },
        previousData,
        hhmIndex
    } = props;

    const summaryContext = useContext(Context);

    const {
        summaryItemsComparisonEnabled
    } = summaryContext;

    const previousIncomes = useMemo(
        () => {
            const index = previousData
                .taxHousehold
                .householdMember
                .findIndex(hhm => hhm.applicantGuid === applicantGuid);

            if(~index) {
                const {
                    incomes
                } = previousData.taxHousehold.householdMember[index];

                return incomes;
            }

            return [];
        },
        [ previousData ]
    );

    const bulkFilter = item => {
        return filterTools.combinedFilterByType(
            filterTools.removeDummyType,
            filterTools.removeSocialSecurityType,
            filterTools.removeUnemploymentType,
        )(item);
    };

    const previousIncomesWithTribalAmount = useMemo(
        () => {
            return previousIncomes
                .filter(bulkFilter)
                .filter(i => i.tribalAmount !== 0);
        },
        [ previousIncomes ]
    );

    const [
        arePreviousTribalIncomesShown,
        togglePreviousTribalIncomesVisibility
    ] = useState(!previousIncomesWithTribalAmount.length);

    const handleToggleLinkClick = e => {
        e.preventDefault();
        togglePreviousTribalIncomesVisibility(isShown => !isShown);
    };

    const getIncomes = (incomes = [], arePrevious = false) => {
        const filteredIncomes = incomes.filter(bulkFilter);

        if(filteredIncomes.length === 0 && arePrevious) {
            return (
                <div className = 'grid-row' >
                    <FormattedMessage id = 'common.no_text' />
                </div>
            );
        }

        return filteredIncomes.map((income, idx) => {
            const {
                frequency,
                sourceName,
                tribalAmount,
                type
            } = income;

            if(tribalAmount !== 0) {
                return (
                    <IncomeItem
                        key = { `${ type }_${ idx }` }
                        id={`tribal-income-hhm${hhmIndex}-row${idx}-col`}
                        type = { INCOME_SOURCES_LIST[type] }
                        sourceName = { sourceName }
                        amount = { currencyTools.getMoneyFormat(tribalAmount, true) }
                        frequency = { FREQUENCIES_LIST[frequency] }
                        isStrikeThroughValue = { arePrevious }
                    />
                );
            }
        });
    };

    const toggleLink = (
        <a
            href = '#'
            onClick = { handleToggleLinkClick }
            className = 'instruction_link instruction_link_p'
        >
            <FormattedMessage id = 'ssap.summary.show_previous_values_link' />
        </a>
    );

    const filterDeductionProps = (expenses = []) => {
        return expenses.map(income => {
            return pick(
                income,
                [
                    'type',
                    'sourceName',
                    'tribalAmount',
                    'frequency'
                ]
            );
        });
    };

    const diffs = useMemo(
        () => {
            return {
                status: !isEqual(filterDeductionProps(incomes), filterDeductionProps(previousIncomes))
            };
        },
        [ incomes, previousIncomes ]
    );

    const linkProps =  (
        summaryItemsComparisonEnabled
        && diffs.status
        && previousIncomesWithTribalAmount.length
    )
        ? {
            auxText: 'common.non_internationalized_text',
            auxTextReplacements: { text: toggleLink }
        }
        : { };

    useSummaryItemDiffRegister({
        diffs: { status: incomes.every(i => i.tribalAmount === 0) ? false : diffs.status },
        summaryContext,
        values: {
            category: 'tribal incomes',
            value: incomes,
            previousValue: previousIncomes,
            textCode: applicantGuid
        }
    });

    if(
        incomes
            .filter(bulkFilter)
            .every(i => i.tribalAmount === 0)
    ) {
        return null;
    }

    // We highlight background only on Final Review page (SSAP in edit mode) if we are tracking a diff
    const displayUpdatedRow = (diffs.status && isFinalSummaryPage && !isSsapInReviewMode)
        ? 'bg-updated_fields'
        : '';

    return (
        <Fragment>
            <SubHeader
                { ...{
                    headerClassName: 'gi-heading-summary summary__header--no-border',
                    text: 'ssap.subheader.amounts_from_tribal_sources',
                    dataTestId: `Tribal-Sources-Header-${applicantGuid}`,
                    id: `tribal-income-hhm${hhmIndex}-subheader`,
                    ...linkProps
                } }
            />
            <div className="grid-row gi-table__responsive">
                <table
                    className="usa-table usa-table--borderless gi-table gi-table__borderless income-table"
                    aria-labelledby={`tribal-income-hhm${hhmIndex}-subheader`}
                >
                    <thead>
                        <IncomeItem
                            id={`tribal-income-hhm${hhmIndex}-header`}
                            dataTestId = { `tribal-income-hhm${hhmIndex}-header` }
                            type = 'common.tribal_source'
                            amount = 'common.how_much_without_question_mark'
                            frequency = 'common.how_often_without_question_mark'
                            design = 'header'
                        />
                    </thead>
                    <tbody>
                        <div className = { displayUpdatedRow } >
                            {
                                arePreviousTribalIncomesShown && getIncomes(previousIncomes, true)
                            }
                            {
                                getIncomes(incomes)
                            }
                        </div>
                    </tbody>
                </table>
            </div>
            
        </Fragment>
    );
};

export default TribalIncomeSummarySection;